<script setup lang="ts">
  // Native V2 printing modal shell: 4 tabs mirroring the desktop V2 modal
  // (Printers / Jobs / Preferences / Help). The support-report block lives
  // in the Help tab (desktop pattern) — no footer, reclaiming phone height.
  import { computed, getCurrentInstance, onMounted, ref } from 'vue';
  import { storeToRefs } from 'pinia';
  import { usePrinting, usePrintingStore } from '@/composition/use-printing';
  import {
    TNativeHelpFlow,
    useNativePrintingUi,
  } from '@/composition/printing/use-native-printing-ui';
  import { PrintPluginStatus } from '@/modules/PrintService';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import V2PrintingTabs, {
    IV2PrintingTab,
  } from '@/components/modals/printing/V2PrintingTabs.vue';
  import V2NativePrintersTab from './V2NativePrintersTab.vue';
  import V2NativeJobsTab from './V2NativeJobsTab.vue';
  import V2NativePreferencesTab from './V2NativePreferencesTab.vue';
  import V2NativeHelpTab from './V2NativeHelpTab.vue';

  const props = defineProps<{
    /** Deep-link: tab to open on (widget "Ver impresiones" → 'jobs'). */
    initialTab?: string;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const printing = usePrinting();
  const store = usePrintingStore();
  const nativeUi = useNativePrintingUi();
  const { lastPrintJobs } = storeToRefs(store);

  onMounted(() => {
    // Discovery + probes kick from the SHELL, not the Printers tab: the
    // printing widget deep-links straight to Jobs, and the Preferences/Help
    // health chips need real probe data no matter which tab the user lands
    // on. Passive reload (never raises the BT permission prompt); both paths
    // run concurrently — chips must never wait on discovery.
    void nativeUi.reload(false);
    nativeUi.probeAll();
  });

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const VALID_TABS = ['printers', 'jobs', 'preferences', 'help'];
  const activeTab = ref(
    props.initialTab && VALID_TABS.includes(props.initialTab)
      ? props.initialTab
      : 'printers'
  );

  /** Flow to auto-open when Help is entered via a shortcut (offline hint). */
  const helpInitialFlow = ref<TNativeHelpFlow | null>(null);

  function selectTab(tab: string) {
    helpInitialFlow.value = null;
    activeTab.value = tab;
  }

  function openHelp(flow: TNativeHelpFlow) {
    helpInitialFlow.value = flow;
    activeTab.value = 'help';
  }

  const isReady = computed(
    () => printing.status.value === PrintPluginStatus.Connected
  );

  // Unknown ("Verificar") counts as attention-needed too: the document may
  // not have printed.
  const failedJobsCount = computed(
    () => lastPrintJobs.value.filter((job) => job.status !== 'printed').length
  );

  const tabs = computed<IV2PrintingTab[]>(() => [
    {
      value: 'printers',
      label: i18nT('printingV2.tabPrinters'),
      icon: 'printer',
      count: 0,
    },
    {
      value: 'jobs',
      label: i18nT('printingV2.tabJobs'),
      icon: 'history',
      count: failedJobsCount.value,
    },
    {
      value: 'preferences',
      label: i18nT('printingV2.tabPreferences'),
      icon: 'tune',
      count: 0,
    },
    {
      value: 'help',
      label: i18nT('printingV2.tabHelp'),
      icon: 'lifebuoy',
      count: 0,
    },
  ]);
</script>

<template>
  <div class="v2-native-printing-modal">
    <div class="v2-native-printing-modal-header">
      <div class="header-main">
        <h2 class="header-title">{{ $t('printingV2.title') }}</h2>

        <PosUiBadge
          v-if="isReady"
          color="success"
          variant="soft"
          size="md"
        >
          <span class="header-status-dot connected" />
          {{ $t('printingV2.nativeReady') }}
        </PosUiBadge>
        <PosUiBadge v-else color="neutral" variant="soft" size="md">
          <span class="header-status-dot" />
          {{ $t('printingV2.disconnected') }}
        </PosUiBadge>
      </div>

      <button
        class="header-close"
        type="button"
        :title="i18nT('printingV2.close')"
        @click="emit('close')"
      >
        <PosUiIcon name="close" :size="22" />
      </button>
    </div>

    <div class="v2-native-printing-modal-tabs">
      <V2PrintingTabs :value="activeTab" :tabs="tabs" @input="selectTab" />
    </div>

    <div class="v2-native-printing-modal-body">
      <V2NativePrintersTab
        v-if="activeTab === 'printers'"
        @open-help="openHelp"
      />
      <V2NativeJobsTab v-else-if="activeTab === 'jobs'" />
      <V2NativePreferencesTab v-else-if="activeTab === 'preferences'" />
      <V2NativeHelpTab
        v-else-if="activeTab === 'help'"
        :initial-flow="helpInitialFlow"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-native-printing-modal {
    @apply tw-flex tw-max-h-[85vh] tw-min-h-[480px] tw-flex-col tw-bg-white;

    .v2-native-printing-modal-header {
      @apply tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-neutral-100 tw-px-5 tw-py-4;

      .header-main {
        @apply tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-3;

        .header-title {
          @apply tw-m-0 tw-text-lg tw-font-bold tw-text-neutral-900;
        }

        .header-status-dot {
          // Spaced from the badge text by a margin, not the badge's flex gap
          // (old POS hardware, Chrome < 84).
          @apply tw-mr-1 tw-h-2 tw-w-2 tw-rounded-full tw-bg-neutral-400;

          &.connected {
            @apply tw-bg-emerald-500;
          }
        }
      }

      .header-close {
        @apply tw-flex tw-h-9 tw-w-9 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-text-neutral-400 tw-transition-colors hover:tw-bg-neutral-100 hover:tw-text-neutral-700;
      }
    }

    .v2-native-printing-modal-tabs {
      @apply tw-px-5 tw-pt-4;
    }

    .v2-native-printing-modal-body {
      @apply tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-5 tw-py-4;
    }
  }
</style>
